// 3rd party imports
import * as React from 'react';
import { inject } from 'mobx-react';

// bernie
import { AnalyticsStore, ContextStore } from 'bernie-plugin-mobx';

// shared-ui
import { LocalizedText } from '@shared-ui/localization-context';

// mobx
import { UserStore, UserAuthStore, PzStore } from 'stores';

// ui
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkHeading, UitkText } from 'uitk-react-text';
import { UitkCard } from 'uitk-react-cards';
import { UitkSecondaryButton } from 'uitk-react-button';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkErrorSummary } from 'uitk-react-error-summary';

// util
import { getUserAnalyticsMicroMessage, analyticsEvent } from '../../util/analytics';
import { modifyLocaleParam } from '../../util/locale-param-transformer';

// observability
import telemeter from '../../observability/telemeter';
import { SignupEvents, SIGNUP_TAG } from '../../observability/events/signup';
import { useLogger } from '@shared-ui/logger-context';
import { UitkLink } from 'uitk-react-link';
import { UitkToast } from 'uitk-react-toast';

interface SignupAHConfirmationProps {
  analytics?: AnalyticsStore;
  context?: ContextStore;
  user?: UserStore;
  userAuth?: UserAuthStore;
  pzStore?: PzStore;
  email: string;
  verificationEmailError?: string;
}

export const SignupAHConfirmation: React.FunctionComponent<SignupAHConfirmationProps> = inject(
  'analytics',
  'userAuth',
  'user',
  'context',
  'pzStore',
)(({ analytics, context, userAuth, user, email, verificationEmailError, pzStore }) => {
  const [resendVerificationEmailError, setResendVerificationEmailError] = React.useState(null);
  const [resentToast, setResentToast] = React.useState(false);

  const LOGGER = useLogger();
  LOGGER.info(`${SIGNUP_TAG}.components`);

  React.useEffect(() => {
    setResendVerificationEmailError(verificationEmailError);
  }, [verificationEmailError]);

  const resendVerificationEmail = async () => {
    analyticsEvent(
      analytics,
      userAuth,
      pzStore,
      'affhub_resend_verification_email.selected',
      'Affhub resend verification email selected',
    );

    try {
      setResentToast(true);
      await user.sendVerificationEmail(email, modifyLocaleParam(context.locale));

      telemeter.increment(SignupEvents.VERIFICATION_EMAIL_RESENT);
      LOGGER.info(SignupEvents.VERIFICATION_EMAIL_RESENT);
    } catch (err) {
      telemeter.increment(SignupEvents.VERIFICATION_EMAIL_RESEND_FAIL);
      LOGGER.error(SignupEvents.VERIFICATION_EMAIL_RESEND_FAIL, JSON.stringify(err), {
        username: email,
      });
      setResendVerificationEmailError('Email communication failed. Please try again');
    }
  };

  return (
    <>
      <UitkSpacing margin={{ blockstart: 'twelve' }}>
        <UitkHeading align="center" tag="h1" size={2}>
          <LocalizedText message="signup.confirmation.title.activation_email_sent" />
        </UitkHeading>
      </UitkSpacing>
      <UitkSpacing padding={{ block: 'twelve' }}>
        <UitkLayoutFlex direction="column">
          <UitkLayoutFlexItem>
            <UitkSpacing margin={{ blockend: 'six' }}>
              <UitkText align="center" size={400}>
                <LocalizedText message="signup.confirmation.message.we_have_sent_an_email" />
              </UitkText>
            </UitkSpacing>
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <UitkSpacing margin={{ blockend: 'six' }}>
              <UitkCard className="break-word" padded={true}>
                <UitkText align="center" size={400} weight="bold">
                  {email}
                </UitkText>
              </UitkCard>
            </UitkSpacing>
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <UitkSpacing margin={{ blockend: 'six' }}>
              <UitkText align="center" size={400}>
                <LocalizedText message="signup.confirmation.message.follow_the_link_within_email" />
              </UitkText>
            </UitkSpacing>
          </UitkLayoutFlexItem>

          {resendVerificationEmailError && (
            <UitkLayoutFlexItem alignSelf="center">
              <UitkSpacing margin={{ block: 'six' }}>
                <UitkErrorSummary heading={resendVerificationEmailError} />
              </UitkSpacing>
            </UitkLayoutFlexItem>
          )}

          <UitkLayoutFlexItem alignSelf="center">
            <UitkSpacing margin={{ blockstart: 'twelve' }}>
              <div>
                <UitkText inline={true} size={300}>
                  <LocalizedText message={'signup.confirmation.didnt_receive_email'} />{' '}
                </UitkText>
                <UitkLink inline={true} onClick={resendVerificationEmail}>
                  <a>
                    <LocalizedText message="signup.confirmation.resend_email" />
                  </a>
                </UitkLink>
              </div>
            </UitkSpacing>
          </UitkLayoutFlexItem>

          <UitkToast
            show={resentToast}
            onDismiss={() => {
              setResentToast(false);
            }}
          >
            <LocalizedText message={'signup.confirmation.activation_email_resent'} />
          </UitkToast>
        </UitkLayoutFlex>
      </UitkSpacing>
    </>
  );
});
